/**
 * Document Reference Generator
 * Generates sequential references like QTN-2526-001, RO-2526-001, DC/2025-26/001 etc.
 */

type DocType = 'quotation' | 'proforma_invoice' | 'purchase_order' | 'customer_invoice' | 'delivery_challan' | 'enquiry' | 'opportunity' | 'project' | 'rfq' | 'bom' | 'work_order' | 'vendor_bill';

const FY = '2526'; // Financial year suffix (2025-26 → 2526)
const FY_SLASH = '2025-26';

const PREFIX_MAP: Record<DocType, string> = {
    quotation: `QTN-${FY}`,
    proforma_invoice: `PI-${FY}`,
    purchase_order: `RO-${FY}`,
    customer_invoice: `INV-${FY}`,
    delivery_challan: `DC/${FY_SLASH}`,
    enquiry: `ENQ-${FY}`,
    opportunity: `OPP-${FY}`,
    project: `PRJ-${FY}`,
    rfq: `RFQ-${FY}`,
    bom: `BOM-${FY}`,
    work_order: `WO-${FY}`,
    vendor_bill: `VB-${FY}`,
};

const SEPARATOR_MAP: Record<DocType, string> = {
    quotation: '-',
    proforma_invoice: '-',
    purchase_order: '-',
    customer_invoice: '-',
    delivery_challan: '/',
    enquiry: '-',
    opportunity: '-',
    project: '-',
    rfq: '-',
    bom: '-',
    work_order: '-',
    vendor_bill: '-',
};

/**
 * Parse the numeric part from an existing reference.
 * e.g. "QTN-2526-007" → 7, "DC/2025-26/004" → 4
 */
function parseSeq(ref: string, docType: DocType): number {
    const sep = SEPARATOR_MAP[docType];
    const parts = ref.split(sep);
    const last = parts[parts.length - 1];
    return parseInt(last, 10) || 0;
}

/**
 * Given an array of existing references for a document type,
 * returns the next auto-incremented reference string.
 */
export function generateNextReference(existingRefs: string[], docType: DocType): string {
    const prefix = PREFIX_MAP[docType];
    const sep = SEPARATOR_MAP[docType];

    let maxSeq = 0;
    for (const ref of existingRefs) {
        if (ref.startsWith(prefix)) {
            const seq = parseSeq(ref, docType);
            if (seq > maxSeq) maxSeq = seq;
        }
    }

    const next = String(maxSeq + 1).padStart(3, '0');
    return `${prefix}${sep}${next}`;
}

/**
 * Hook-friendly function: pass the react-admin `getList` result refs.
 */
export function getNextRef(docType: DocType, existingRefs: string[]): string {
    return generateNextReference(existingRefs, docType);
}
